import makeStyles from '@mui/styles/makeStyles';
import {isEmpty} from 'lodash';
import {Trash2} from "lucide-react";
import React, {useEffect, useState} from "react";
import {useTranslation} from "react-i18next";
import {initials} from "common/helpers";
import {color, radius, type} from "common/tokens";
import Question from "components/Question";
import CtaButton from "components/redesign/CtaButton";
import Dialog from "components/redesign/Dialog";
import questionFields from "components/redesign/questionFields";
import ShimmerImage from "components/redesign/ShimmerImage";

const useStyles = makeStyles(theme => ({
  form: {
    ...questionFields,
    display: 'flex',
    flexDirection: 'column',
    gap: 4,
  },
  pictureRow: {
    display: 'flex',
    alignItems: 'center',
    gap: 24,
    paddingBottom: 12,
    [theme.breakpoints.down('sm')]: {
      flexDirection: 'column',
      alignItems: 'flex-start',
      gap: 12,
    },
  },
  pictureBox: {
    position: 'relative',
    flexShrink: 0,
  },
  picture: {
    width: 104,
    height: 104,
    borderRadius: radius.PILL,
    background: color.ACCENT_TINT,
    border: `1px solid ${color.DIVIDER}`,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    boxSizing: 'border-box',
    overflow: 'hidden',
  },
  pictureInitials: {
    ...type.panelTitle,
    color: color.TEXT_MUTED,
  },
  // Only offered once there is a picture to remove, and put on the picture
  // itself rather than as a line of red text under the upload field.
  removePicture: {
    position: 'absolute',
    top: -4,
    right: -4,
    width: 28,
    height: 28,
    borderRadius: radius.PILL,
    border: `1px solid ${color.DIVIDER}`,
    background: color.SURFACE,
    color: color.DANGER,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: 0,
    cursor: 'pointer',
  },
  pictureActions: {
    flex: '1 1 0',
    minWidth: 0,
  },
  names: {
    display: 'grid',
    gridTemplateColumns: 'minmax(0, 1fr) minmax(0, 1fr)',
    gap: 16,
    [theme.breakpoints.down('sm')]: {
      gridTemplateColumns: 'minmax(0, 1fr)',
    },
  },
  spacer: {
    flex: 1,
  },
}));

const EditUserProfile = ({open, handleClose, saveProfile, bio, firstName, lastName, picture, phoneNumber}) => {
  const classes = useStyles();
  const {t} = useTranslation();

  const firstNameQuestion = {label: t('firstName'), questionType: 'Text', mandatory: true};
  const lastNameQuestion = {label: t('lastName'), questionType: 'Text', mandatory: true};
  const aboutYouQuestion = {label: t('aboutYou'), questionType: 'TextBox'};
  const imageQuestion = {label: t('profilePicture'), questionType: 'SingleImageUpload'};
  const phoneNumberQuestion = {label: t('phoneNumber'), questionType: 'PhoneNumber', mandatory: true};

  const [values, setValues] = useState({});
  const [errors, setErrors] = useState({});

  const validate = () => {
    const newErrors = {};
    if (!values.firstName || !values.firstName.trim()) {
      newErrors.firstName = t('firstNameRequired');
    }
    if (!values.lastName || !values.lastName.trim()) {
      newErrors.lastName = t('lastNameRequired');
    }
    if (!values.phoneNumber || !values.phoneNumber.trim()) {
      newErrors.phoneNumber = t('phoneNumberRequired');
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = () => {
    if (!validate()) return;

    const payload = {...values};

    if (payload.tempPicture === picture) {
      delete payload.picture;
    }

    if (payload.tempPicture === "") {
      payload.picture = "null";
    }

    saveProfile(payload);
    handleClose();
  };

  useEffect(() => {
    setValues({firstName, lastName, bio, picture, tempPicture: picture, phoneNumber});
  }, []);

  const handleChange = name => value => {
    setValues({...values, [name]: value});
    setErrors(prev => ({...prev, [name]: undefined}));
  };

  const handleImage = (image, file) => {
    if (!file && image === "") {
      setValues({...values, tempPicture: "", picture: "null"});
    } else if (file) {
      setValues({...values, tempPicture: image, picture: file});
    } else {
      setValues({...values, tempPicture: image});
    }
  };

  const footer = (
    <>
      <span className={classes.spacer}/>
      <CtaButton size={'large'} variant={'secondary'} label={t('cancel')} onClick={handleClose}/>
      <CtaButton size={'large'} label={t('confirmChanges')} onClick={handleSubmit}/>
    </>
  );

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      title={t('editProfile')}
      maxWidth={640}
      dismissable={false}
      footer={footer}
    >
      <div className={classes.form}>
        <div className={classes.pictureRow}>
          <div className={classes.pictureBox}>
            {isEmpty(values.tempPicture) ? (
              <div className={classes.picture}>
                <span className={classes.pictureInitials}>
                  {initials(`${firstName} ${lastName}`)}
                </span>
              </div>
            ) : (
              <>
                <ShimmerImage src={values.tempPicture} className={classes.picture}/>
                <button
                  type={'button'}
                  className={classes.removePicture}
                  aria-label={t('removeProfilePicture')}
                  title={t('removeProfilePicture')}
                  onClick={() => handleImage("")}
                >
                  <Trash2 size={14}/>
                </button>
              </>
            )}
          </div>
          <div className={classes.pictureActions}>
            <Question question={imageQuestion} onAnswered={handleImage}/>
          </div>
        </div>

        <div className={classes.names}>
          <Question question={firstNameQuestion} onAnswered={handleChange("firstName")}
                    value={values.firstName} error={errors.firstName} showBorder/>
          <Question question={lastNameQuestion} onAnswered={handleChange("lastName")}
                    value={values.lastName} error={errors.lastName} showBorder/>
        </div>
        <Question question={phoneNumberQuestion} onAnswered={handleChange("phoneNumber")}
                  value={values.phoneNumber} error={errors.phoneNumber} showBorder/>
        <Question question={aboutYouQuestion} onAnswered={handleChange("bio")} value={values.bio} showBorder/>
      </div>
    </Dialog>
  );
};

export default EditUserProfile;
